/**
 * KNK-1.7 / FR-45 — Offices: mark a team as an Office with a timezone, and place teams under it.
 *
 * ⚠️ Nothing here is the gate: `set_team_office` / `set_team_office_parent` refuse a member-holding
 * team as an Office and a placement that would give a rep two Offices, with a sentence this card
 * shows as it is. Rules and copy live in `lib/fieldProfiles.ts`.
 */
import { useCallback, useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/components/ui/use-toast';
import { FIELD_PROFILES_COPY as COPY, OFFICE_TIMEZONES, officeTree, type OfficeTeam } from '@/lib/fieldProfiles';
import { fetchOfficeTeams, setTeamOffice, setTeamOfficeParent } from '@/services/fieldProfileService';

const NONE = '__none__';

/** The listed zones, plus whatever an Office already holds that the list lacks. */
const zonesWith = (current: string | null) =>
  current && !OFFICE_TIMEZONES.includes(current) ? [current, ...OFFICE_TIMEZONES] : OFFICE_TIMEZONES;

export function OfficesCard({ onChanged }: { onChanged?: () => void }) {
  const { toast } = useToast();
  const [teams, setTeams] = useState<OfficeTeam[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [newOffice, setNewOffice] = useState<string>('');
  const [newZone, setNewZone] = useState<string>('America/Phoenix');

  const load = useCallback(async () => {
    try {
      setTeams(await fetchOfficeTeams());
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const run = async (key: string, action: () => Promise<void>) => {
    setBusy(key);
    try {
      await action();
      await load();
      onChanged?.();
    } catch (e) {
      toast({ title: 'Not changed', description: e instanceof Error ? e.message : String(e), variant: 'destructive' });
    } finally {
      setBusy(null);
    }
  };

  const tree = officeTree(teams ?? []);
  const candidates = tree.unplaced;

  return (
    <Card data-testid="knock-offices">
      <CardHeader>
        <CardTitle>{COPY.officesTitle}</CardTitle>
        <CardDescription>{COPY.officesDescription}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {error && (
          <Alert variant="destructive">
            <AlertDescription>Could not read teams: {error}</AlertDescription>
          </Alert>
        )}
        {teams === null && !error && <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" aria-label="Loading" />}

        {teams !== null && (
          <>
            {tree.offices.length === 0 && <p className="text-sm text-muted-foreground">{COPY.noOffices}</p>}

            <ul className="space-y-3">
              {tree.offices.map(({ office, teams: under }) => (
                <li key={office.id} className="space-y-3 rounded-md border border-border p-4" data-testid={`office-${office.id}`}>
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-foreground">{office.name}</span>
                      <Badge variant="secondary">Office</Badge>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Select
                        value={office.timezone ?? ''}
                        onValueChange={(tz) => void run(`tz-${office.id}`, () => setTeamOffice(office.id, true, tz))}
                        disabled={busy !== null}
                      >
                        <SelectTrigger className="w-52" aria-label={`Timezone of ${office.name}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {zonesWith(office.timezone).map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={busy !== null}
                        onClick={() => void run(`un-${office.id}`, () => setTeamOffice(office.id, false, null))}
                      >
                        {busy === `un-${office.id}` && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        {COPY.unmarkOffice}
                      </Button>
                    </div>
                  </div>
                  {under.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No teams under it yet.</p>
                  ) : (
                    <ul className="divide-y divide-border rounded-md border border-border">
                      {under.map((t) => (
                        <TeamRow key={t.id} team={t} offices={tree.offices.map((o) => o.office)} busy={busy} run={run} />
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>

            {candidates.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium text-foreground">{COPY.unplaced}</p>
                <ul className="divide-y divide-border rounded-md border border-border">
                  {candidates.map((t) => (
                    <TeamRow key={t.id} team={t} offices={tree.offices.map((o) => o.office)} busy={busy} run={run} />
                  ))}
                </ul>
              </div>
            )}

            <div className="space-y-2 rounded-md border border-dashed border-border p-4">
              <p className="text-sm font-medium text-foreground">{COPY.markOffice}</p>
              <p className="text-xs text-muted-foreground">{COPY.emptyTeamsOnly}</p>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                <div className="space-y-1.5">
                  <Label htmlFor="office-new-team">Team</Label>
                  <Select value={newOffice} onValueChange={setNewOffice}>
                    <SelectTrigger id="office-new-team" className="w-full sm:w-56"><SelectValue placeholder="Choose a team" /></SelectTrigger>
                    <SelectContent>
                      {candidates.map((t) => <SelectItem key={t.id} value={String(t.id)}>{t.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="office-new-zone">Timezone</Label>
                  <Select value={newZone} onValueChange={setNewZone}>
                    <SelectTrigger id="office-new-zone" className="w-full sm:w-52"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {OFFICE_TIMEZONES.map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  disabled={busy !== null || newOffice === ''}
                  onClick={() => void run('mark', async () => {
                    await setTeamOffice(Number(newOffice), true, newZone);
                    setNewOffice('');
                  })}
                >
                  {busy === 'mark' && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {COPY.markOffice}
                </Button>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function TeamRow({
  team,
  offices,
  busy,
  run,
}: {
  team: OfficeTeam;
  offices: readonly OfficeTeam[];
  busy: string | null;
  run: (key: string, action: () => Promise<void>) => Promise<void>;
}) {
  const placed = team.officeId !== null && offices.some((o) => o.id === team.officeId);
  return (
    <li className="flex flex-col gap-2 p-3 sm:flex-row sm:items-center sm:justify-between" data-testid={`office-team-${team.id}`}>
      <span className="text-sm text-foreground">{team.name}</span>
      <Select
        value={placed ? String(team.officeId) : NONE}
        disabled={busy !== null || offices.length === 0}
        onValueChange={(v) => void run(`place-${team.id}`, () => setTeamOfficeParent(team.id, v === NONE ? null : Number(v)))}
      >
        <SelectTrigger className="w-full sm:w-52" aria-label={`${COPY.placeUnder} for ${team.name}`}><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE}>{COPY.noOffice}</SelectItem>
          {offices.map((o) => <SelectItem key={o.id} value={String(o.id)}>{o.name}</SelectItem>)}
        </SelectContent>
      </Select>
    </li>
  );
}
